import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import styled from '@emotion/styled'
import { Virtuoso } from "react-virtuoso";
import { useDispatch, useSelector } from "react-redux";
import { deleteChatMessage, getAdminSupport, sendChatFile, sendSupportMessage } from "../../store/chat/chatActions";
import { IoTextOutline } from "react-icons/io5";
import { FiArrowRight, FiCalendar, FiDownload, FiFile, FiPaperclip, FiSend, FiTrash, FiUploadCloud, FiX } from "react-icons/fi";
import { SocketContext } from "../../utils/socketConfig";
import { errorToast, getUploadedFileUrl } from "../../utils";
import moment from "moment";
import { Link } from 'react-router-dom';

const SUPPORT_SENDER_ID =
    process.env.REACT_APP_SYSTEM_ID || '420420420420420420420420';

function ChatList({ userId, messages = [], onSend, onDelete, pushFile }) {

    const [type, setType] = useState(0)
    const ref = useRef(null);
    const messageArea = useRef(null);
    const dispatch = useDispatch();
    const socket = useContext(SocketContext);
    const [file, setFile] = useState(null);
    const [isDelete, setIsDelete] = useState(-1);
    const [sending, setSending] = useState(false);
    const fileInputRef = useRef(null);
    const [fileDragDepth, setFileDragDepth] = useState(0);

    const MAX_FILE_BYTES = 2 * 1024 * 1024;

    const fileSizeError = () => {
        errorToast("Max file size is 2 MB");
    };

    const validatePickedFile = (f) => {
        if (!f) return false;
        if (f.size > MAX_FILE_BYTES) {
            fileSizeError();
            return false;
        }
        const lower = f.name.toLowerCase();
        if (!/\.(jpe?g|png|gif|pdf)$/.test(lower)) {
            errorToast("Use JPG, PNG, GIF, or PDF.");
            return false;
        }
        return true;
    };

    const assignFileFromList = (list) => {
        const f = list?.[0];
        if (f && validatePickedFile(f)) setFile(f);
    };

    useEffect(() => {
        if (!socket) return;

        const handleMessage = (v) => onSend(v);
        const handleDelete = (v) => onDelete(v);

        socket.on("message", handleMessage);
        socket.on("DELETE_MSG", handleDelete);

        return () => {
            socket.off("message", handleMessage);
            socket.off("DELETE_MSG", handleDelete);
        };
    }, [socket, onSend, onDelete])

    const deleteMessage = (index) => {
        setIsDelete(index);
    }

    const confirmDelete = (id) => {
        dispatch(deleteChatMessage(id))
            .unwrap()
            .then((v) => {
                onDelete(v)
            })
    }

    const cancelDelete = () => {
        setIsDelete(-1);
    }

    const sendMessage = async () => {
        if (sending) return;
        if (type === 0) {
            const raw = messageArea.current?.value?.trim();
            if (!raw) return;
            setSending(true);
            try {
                const v = await dispatch(sendSupportMessage(raw)).unwrap();
                messageArea.current.value = "";
                onSend(v);
                dispatch(getAdminSupport());
            } catch (e) {
                console.error(e);
            } finally {
                setSending(false);
            }
        } else if (type === 1) {
            if (file) {
                setSending(true);
                try {
                    const v = await dispatch(sendChatFile(file)).unwrap();
                    setType(0);
                    setFile(null);
                    onSend(v);
                    dispatch(getAdminSupport());
                } catch (e) {
                    console.error(e);
                } finally {
                    setSending(false);
                }
            }
        }
    }

    const handleTextKeyDown = (e) => {
        if (e.key !== 'Enter' || e.shiftKey) return;
        e.preventDefault();
        void sendMessage();
    };

    const Message = ({ rowData, fromUser, showDate, index }) => {
        let time = moment(rowData.createdAt)

        return (
            <>
                {showDate && <p className='justify-self-center mb-5 bg-nt-grey-light rounded-xl px-2 py-1 mx-auto w-fit'>{time.format("YYYY-MM-DD")}</p>}
                <div className={`${fromUser ? "max-w-[80%] justify-self-end pb-2 pe-2" : "justify-self-start max-w-[80%] pb-2"}`}>
                    <div className='flex gap-2 relative items-end'>
                        {(fromUser && isDelete !== index && !rowData.isReschedule && !rowData.isCancel) && <span className="min-w-[30px]">
                            <FiTrash onClick={() => deleteMessage(index)}
                                className="ml-2 text-nt-red cursor-pointer" size={20} />
                        </span>}
                        {(fromUser && isDelete === index) && <span className="min-w-[30px]">
                            <span className="absolute bottom-0 left-0 bg-nt-grey-light px-2 rounded bg-opacity-70">
                                <span className="cursor-pointer" onClick={() => confirmDelete(rowData._id)}>Yes</span> - <span className="cursor-pointer" onClick={() => cancelDelete()}>No</span>
                            </span>
                        </span>}
                        {rowData.isReschedule ?
                            <div className={`rounded-xl bg-nt-grey-light text-nt-blue px-2 py-2 w-full flex items-center`}>
                                <div className="border-r-2 pr-2 border-nt-grey">
                                    <h5 className="font-medium flex gap-1 items-center"><FiCalendar />Rescheduled Course</h5>
                                    {rowData.message}
                                </div>
                                <Link to={"/appointment/detail/" + rowData.reschedule}>
                                    <FiArrowRight className="ml-2" size={20} />
                                </Link>
                            </div> :
                            rowData.isCancel ?
                            <div className={`rounded-xl bg-nt-grey-light text-nt-blue px-2 py-2 w-full flex items-center`}>
                                <div className="border-r-2 pr-2 border-nt-grey">
                                    <h5 className="font-medium flex gap-1 items-center"><FiCalendar />Canceled Course</h5>
                                    {rowData.message}
                                </div>
                                <Link to={"/appointment/detail/" + rowData.reschedule}>
                                    <FiArrowRight className="ml-2" size={20} />
                                </Link>
                            </div>:
                            <p className={`rounded-2xl shadow-sm bg-nt-blue text-white px-3 py-2 w-full leading-relaxed`} dangerouslySetInnerHTML={{__html: rowData.message.replace("\n", "<br/>")}}></p>}
                    </div>
                    <p className="float-right text-xs text-gray-500 mt-0.5">{time.format("HH:mm")}</p>
                </div>
            </>
        )
    }

    const File = ({ rowData, fromUser, showDate, index }) => {
        let time = new Date(rowData.createdAt);
        const meta =
            rowData.file != null &&
            typeof rowData.file === 'object' &&
            !Array.isArray(rowData.file)
                ? rowData.file
                : null;
        const displayName =
            meta?.originalName || meta?.originalname || meta?.name || 'Attachment';
        const filePath = meta?.path;
        const href = filePath ? getUploadedFileUrl(filePath) : '#';
        const downloadName =
            meta?.originalName ||
            meta?.originalname ||
            meta?.name ||
            'download';

        return (
            <>
                {showDate && <p className='justify-self-center mb-5 bg-nt-grey-light rounded-xl px-2 py-1 mx-auto w-fit'>{time.getFullYear() + "-" + time.getMonth() + "-" + time.getDate()}</p>}
                <div className={`${fromUser ? "max-w-[80%] justify-self-end pb-2 pe-2" : "justify-self-start max-w-[80%] pb-2"}`}>
                    <div className='flex gap-2 relative items-end'>
                        {(fromUser && isDelete !== index) && <span className="min-w-[30px]">
                            <FiTrash onClick={() => deleteMessage(index)}
                                className="ml-2 text-nt-red cursor-pointer" size={20} />
                        </span>}

                        {(fromUser && isDelete === index) && <span className="min-w-[30px]">
                            <span className="absolute bottom-0 left-0 bg-nt-grey-light px-2 rounded bg-opacity-70">
                                <span className="cursor-pointer" onClick={() => confirmDelete(rowData._id)}>Yes</span> - <span className="cursor-pointer" onClick={() => cancelDelete()}>No</span>
                            </span>
                        </span>}

                        <a href={href} download={downloadName}
                            className={`no-underline group inline-flex w-full max-w-full items-center gap-2 rounded-2xl px-3 py-2.5 shadow-sm transition hover:opacity-95 ${fromUser ? 'bg-nt-blue text-white' : 'border border-gray-200/90 bg-white text-nt-blue'} ${filePath ? 'cursor-pointer' : 'cursor-default opacity-90'}`}
                            onClick={(e) => { if (!filePath) e.preventDefault(); }}
                        >
                            <FiFile className={`shrink-0 ${fromUser ? 'text-white/90' : 'text-nt-blue/70'}`} size={18} aria-hidden />
                            <span className={`min-w-0 flex-1 truncate text-sm font-medium ${fromUser ? '' : 'text-nt-blue'}`}>{displayName}</span>
                            <FiDownload className={`shrink-0 ${fromUser ? 'text-white' : 'text-nt-blue'}`} size={18} aria-hidden />
                        </a>
                    </div>
                    <p className="float-right">{time.getHours() + ':' + time.getMinutes()}</p>
                </div>
            </>
        )
    }

    const Deleted = ({ rowData, fromUser, showDate, index }) => {
        let time = new Date(rowData.createdAt);
        return (
            <>
                {showDate && <p className='justify-self-center mb-5 bg-nt-grey-light rounded-xl px-2 py-1 mx-auto w-fit'>{time.getFullYear() + "-" + time.getMonth() + "-" + time.getDate()}</p>}
                <div className={`${fromUser ? "max-w-[80%] justify-self-end pb-2 pe-2" : "justify-self-start max-w-[80%] pb-2"}`}>
                    <div className='flex gap-2 items-end'>
                        Deleted Message
                    </div>
                </div>
            </>
        )
    }

    const row = React.useMemo(
        () => (i, message) => {
            const fromUser = message.sender === userId;
            if (message.hidden)
                return <Deleted index={i} showDate={i == 0 || new Date(message.createdAt).getDate() - new Date(messages[i - 1].createdAt).getDate() > 0} rowData={message} fromUser={fromUser} />
            return !message.isFile ?
                <Message index={i} showDate={i == 0 || new Date(message.createdAt).getDate() - new Date(messages[i - 1].createdAt).getDate() > 0} rowData={message} fromUser={fromUser} />
                : <File index={i} showDate={i == 0 || new Date(message.createdAt).getDate() - new Date(messages[i - 1].createdAt).getDate() > 0} rowData={message} fromUser={fromUser} />
        },
        [messages.length, isDelete, userId]
    );

    const Item = styled.div`
        display:grid;
        width:100%;
    `;
    const fileDropActive = fileDragDepth > 0;
    return (
        <div className="flex grow flex-col gap-2">
            <Virtuoso
                ref={ref}
                className="flex-1 pb-5 min-h-[200px]"
                initialTopMostItemIndex={messages.length - 1}
                followOutput="smooth"
                components={{ Item }}
                itemContent={row}
                data={messages}
            />
            <div className='flex mt-2 gap-2 items-center'>
                {type === 0 &&
                    <form
                        className="w-full flex flex-col gap-1"
                        onSubmit={(e) => {
                            e.preventDefault();
                            void sendMessage();
                        }}
                    >
                        <textarea
                            ref={messageArea}
                            disabled={sending}
                            onKeyDown={handleTextKeyDown}
                            placeholder="Type a message…"
                            rows={3}
                            className="p-3 border-2 border-nt-blue rounded-2xl min-h-[100px] w-full resize-y outline-none focus:ring-2 focus:ring-nt-blue/30 transition-shadow disabled:opacity-60"
                        />
                        <p className="font-medium text-xs text-gray-500 m-0">Enter to send · Shift+Enter for new line</p>
                    </form>
                }
                {type === 1 ? (
                    <form
                        className="w-full min-w-0 flex flex-col gap-1.5"
                        onSubmit={(e) => {
                            e.preventDefault();
                            void sendMessage();
                        }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            accept=".jpg,.jpeg,.png,.gif,.pdf,image/jpeg,image/png,image/gif,application/pdf"
                            onChange={(e) => {
                                assignFileFromList(e.target.files);
                                e.target.value = "";
                            }}
                        />
                        {!file ? (
                            <button
                                type="button"
                                disabled={sending}
                                onClick={() => fileInputRef.current?.click()}
                                onDragEnter={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setFileDragDepth((d) => d + 1);
                                }}
                                onDragLeave={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setFileDragDepth((d) => Math.max(0, d - 1));
                                }}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setFileDragDepth(0);
                                    assignFileFromList(e.dataTransfer.files);
                                }}
                                className={`flex min-h-[100px] w-full flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed px-4 text-center outline-none transition-all disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-nt-blue/35 ${
                                    fileDropActive
                                        ? "border-nt-blue bg-blue-50/50 shadow-inner"
                                        : "border-nt-blue/35 bg-white hover:border-nt-blue/60 hover:bg-slate-50/80"
                                }`}
                            >
                                <FiUploadCloud
                                    className="text-nt-blue"
                                    size={28}
                                    strokeWidth={1.35}
                                    aria-hidden
                                />
                                <span className="text-sm font-semibold text-nt-blue">
                                    Drop a file here or browse
                                </span>
                                <span className="text-xs text-gray-500">
                                    JPG, PNG, GIF, or PDF · up to 2&nbsp;MB
                                </span>
                            </button>
                        ) : (
                            <div className="flex min-h-[100px] flex-col justify-center gap-3 rounded-2xl border-2 border-nt-blue bg-white px-4 py-3 shadow-sm">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-nt-blue">
                                        <FiFile size={20} aria-hidden />
                                    </div>
                                    <div className="min-w-0 flex-1 text-left">
                                        <p className="truncate text-sm font-semibold text-gray-900">
                                            {file.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {file.size >= 1024 * 1024
                                                ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                                                : `${Math.max(1, Math.round(file.size / 1024))} KB`}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        title="Remove"
                                        disabled={sending}
                                        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-gray-500 transition hover:bg-red-50 hover:text-nt-red"
                                        onClick={() => setFile(null)}
                                    >
                                        <FiX size={18} aria-hidden />
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    disabled={sending}
                                    className="self-start text-xs font-medium text-nt-blue underline-offset-2 hover:underline"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    Choose a different file
                                </button>
                            </div>
                        )}
                    </form>
                ) : null}
                <div className='flex flex-col ps-2 items-center justify-between gap-2 shrink-0'>
                    <button
                        type="button"
                        title="Send"
                        disabled={sending || (type === 1 && !file)}
                        className='bg-nt-blue text-white cursor-pointer p-2 rounded-xl disabled:opacity-50 shadow-sm hover:opacity-95'
                        onClick={() => void sendMessage()}
                    >
                        <FiSend color='#fff' size={20} />
                    </button>
                    <div
                        className='bg-nt-grey-light cursor-pointer text-nt-blue p-2 rounded-xl'
                        onClick={() => {
                            if (type === 1) {
                                setFile(null);
                                setFileDragDepth(0);
                                setType(0);
                            } else setType(1);
                        }}
                    >
                        {type == 1 ? <IoTextOutline size={20} /> : <FiPaperclip size={20} />}
                    </div>
                </div>
            </div>
        </div>
    );
}

function normalizeMongoId(v) {
    if (v == null) return '';
    if (typeof v === 'object' && v._id != null) return String(v._id);
    return String(v);
}

const VirtuosoMsg = ({ messagesList, pushFile }) => {
    const [messages, setMessages] = React.useState(() => (Array.isArray(messagesList) ? messagesList : []));
    const currentChat = useSelector((state) => state.chat.currentChat);

    useEffect(() => {
        const next = currentChat?.messages;
        if (Array.isArray(next)) {
            setMessages([...next]);
        } else {
            setMessages([]);
        }
    }, [currentChat?._id, currentChat?.messages]);

    const onSend = useCallback((message) => {
        const msgChat = normalizeMongoId(message?.chat);
        const activeChat = normalizeMongoId(currentChat?._id);
        if (msgChat && activeChat && msgChat === activeChat) {
            setMessages((x) => [
                ...x,
                { ...message, sender: normalizeMongoId(message?.sender) },
            ]);
        }
    }, [currentChat?._id]);

    const onDelete = useCallback((msg) => {
        setMessages((v) => v.map((m) => (m._id === msg._id ? msg : m)));
    }, []);

    return (
        <div className="w-full grow flex flex-col">
            <ChatList
                pushFile={(v) => pushFile(v)}
                messages={messages}
                userId={SUPPORT_SENDER_ID}
                onSend={onSend}
                onDelete={onDelete}
            />
        </div>
    );
}
export { VirtuosoMsg }
